/**
 * ApiLogsPanel
 * Panel que muestra las llamadas a API capturadas.
 */
import React, { useState, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Chip,
  Paper,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Collapse,
  Divider,
} from "@mui/material";
import {
  Search as SearchIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  ArrowUpward as RequestIcon,
  ArrowDownward as ResponseIcon,
  Error as ErrorIcon,
  CheckCircle as SuccessIcon,
} from "@mui/icons-material";

const HTTP_METHOD_COLORS = {
  GET: "info",
  POST: "success",
  PUT: "warning",
  PATCH: "warning",
  DELETE: "error",
};

const ApiLogEntry = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  const isRequest = log.type === "request";
  const isError = log.type === "response_error" || log.type === "request_error";
  const isResponse = log.type === "response" || log.type === "response_error";

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      fractionalSecondDigits: 3,
    });
  };

  const getStatusColor = (status) => {
    if (!status) return "default";
    if (status >= 200 && status < 300) return "success";
    if (status >= 300 && status < 400) return "info";
    if (status >= 400 && status < 500) return "warning";
    return "error";
  };

  const formatJson = (data) => {
    if (!data) return "null";
    try {
      return JSON.stringify(data, null, 2);
    } catch {
      return String(data);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        mb: 1,
        bgcolor: isError ? "error.50" : isRequest ? "grey.50" : "success.50",
        borderLeft: 4,
        borderColor: isError ? "error.main" : isRequest ? "grey.400" : "success.main",
        "&:hover": { bgcolor: "action.hover" },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {/* Type Icon */}
        <Tooltip title={isRequest ? "Request" : "Response"}>
          {isRequest ? (
            <RequestIcon color="action" />
          ) : isError ? (
            <ErrorIcon color="error" />
          ) : (
            <SuccessIcon color="success" />
          )}
        </Tooltip>

        {/* Method */}
        {log.method && (
          <Chip
            size="small"
            label={log.method}
            color={HTTP_METHOD_COLORS[log.method] || "default"}
            sx={{ minWidth: 60, fontWeight: "bold" }}
          />
        )}

        {/* Status (for responses) */}
        {isResponse && log.status && (
          <Chip
            size="small"
            label={log.status}
            color={getStatusColor(log.status)}
            variant="outlined"
          />
        )}

        {/* Duration */}
        {log.duration && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ minWidth: 60 }}
          >
            {log.duration}
          </Typography>
        )}

        {/* Timestamp */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ minWidth: 100, fontFamily: "monospace" }}
        >
          {formatTimestamp(log.timestamp)}
        </Typography>

        {/* URL */}
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "monospace",
              fontSize: "0.85rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {log.url || log.fullUrl}
          </Typography>
          {isError && log.errorMessage && (
            <Typography variant="caption" color="error.main">
              {log.errorMessage}
            </Typography>
          )}
        </Box>

        {/* Expand Button */}
        <Tooltip title={expanded ? "Colapsar" : "Ver detalles"}>
          <IconButton size="small" onClick={() => setExpanded(!expanded)}>
            {expanded ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </Tooltip>
      </Stack>

      {/* Expanded Content */}
      <Collapse in={expanded}>
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: "background.paper",
            borderRadius: 1,
            maxHeight: 400,
            overflow: "auto",
          }}
        >
          <Stack spacing={2}>
            {/* Full URL */}
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight="bold">
                URL Completa:
              </Typography>
              <Typography
                variant="body2"
                sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
              >
                {log.fullUrl || log.url}
              </Typography>
            </Box>

            {/* Headers (for requests) */}
            {log.headers && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Headers:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    bgcolor: "grey.100",
                    p: 1,
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 150,
                  }}
                >
                  {formatJson(log.headers)}
                </Box>
              </Box>
            )}

            {/* Params (for requests) */}
            {log.params && Object.keys(log.params).length > 0 && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  Params:
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    bgcolor: "grey.100",
                    p: 1,
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 150,
                  }}
                >
                  {formatJson(log.params)}
                </Box>
              </Box>
            )}

            {/* Request/Response Data */}
            {(log.data || log.responseData) && (
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight="bold">
                  {isRequest ? "Request Body:" : "Response Data:"}
                </Typography>
                <Box
                  component="pre"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    bgcolor: isError ? "error.50" : "grey.100",
                    p: 1,
                    borderRadius: 1,
                    overflow: "auto",
                    maxHeight: 250,
                  }}
                >
                  {formatJson(log.data || log.responseData)}
                </Box>
              </Box>
            )}
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

const ApiLogsPanel = ({ logs }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");

  const filteredLogs = useMemo(() => {
    let result = [...logs].reverse(); // Mostrar más recientes primero

    if (typeFilter !== "all") {
      if (typeFilter === "request") {
        result = result.filter((log) => log.type === "request");
      } else if (typeFilter === "response") {
        result = result.filter((log) => log.type === "response" || log.type === "response_error");
      } else if (typeFilter === "error") {
        result = result.filter((log) => log.type === "response_error" || log.type === "request_error");
      }
    }

    if (methodFilter !== "all") {
      result = result.filter((log) => log.method === methodFilter);
    }

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (log) =>
          log.url?.toLowerCase().includes(term) ||
          log.fullUrl?.toLowerCase().includes(term) ||
          JSON.stringify(log.data || {}).toLowerCase().includes(term)
      );
    }

    return result;
  }, [logs, searchTerm, typeFilter, methodFilter]);

  if (logs.length === 0) {
    return (
      <Box
        sx={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          p: 4,
        }}
      >
        <RequestIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          No hay llamadas API capturadas
        </Typography>
        <Typography variant="body2" color="text.disabled">
          Activa el modo debug para comenzar a capturar llamadas
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ p: 2, bgcolor: "background.paper" }}>
        <TextField
          size="small"
          placeholder="Buscar en URL o data..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="request">Requests</MenuItem>
            <MenuItem value="response">Responses</MenuItem>
            <MenuItem value="error">Errores</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <Select
            value={methodFilter}
            onChange={(e) => setMethodFilter(e.target.value)}
          >
            <MenuItem value="all">Method</MenuItem>
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
            <MenuItem value="PUT">PUT</MenuItem>
            <MenuItem value="PATCH">PATCH</MenuItem>
            <MenuItem value="DELETE">DELETE</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {/* Logs List */}
      <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
        {filteredLogs.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
            No se encontraron llamadas con los filtros aplicados
          </Typography>
        ) : (
          filteredLogs.map((log) => <ApiLogEntry key={log.id} log={log} />)
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ p: 1, borderTop: 1, borderColor: "divider", bgcolor: "background.paper" }}>
        <Typography variant="caption" color="text.secondary">
          Mostrando {filteredLogs.length} de {logs.length} llamadas API
        </Typography>
      </Box>
    </Box>
  );
};

export default ApiLogsPanel;
